import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import QRCode from "qrcode";
import { toast } from "sonner";
import api, { apiError, fcfa, API } from "../lib/api";
import { getCurrentPosition, acquireWakeLock } from "../lib/geo";
import { ExpensesCard } from "../components/ExpensesCard";
import { SignContract } from "../components/SignContract";
import { PromoPoster } from "../components/PromoPoster";
import { MeetingRoom } from "../components/MeetingRoom";
import { enablePush, pushSupported, notifPermission } from "../lib/push";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Users, ArrowLeft, ShieldCheck, Download, Printer, Copy, Send, Trash2,
  Target, MapPin, TrendingUp, Bike, LogOut, KeyRound, Phone, Mail, MessageCircle, Trophy, Bell, Video, PenLine, AlertTriangle, Lock,
} from "lucide-react";

const ACCENT = "#D94F2B";

export default function AgentSpace() {
  const { user } = useAuth();
  if (user && user.role === "agent") return <AgentDashboard />;
  return <AgentLogin />;
}

/* ---------------- Login (OTP first time + PIN afterwards) ---------------- */
function AgentLogin() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState("email"); // email | emailcode | teamcode
  const [email, setEmail] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [demoCode, setDemoCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState(""); // code responsable hebdo (team-code)

  const onSendEmail = async () => {
    const e = email.trim().toLowerCase();
    if (!e || !e.includes("@")) return toast.error("Entrez votre email");
    setLoading(true);
    try {
      const { data } = await api.post("/agent/email/send-code", { email: e });
      setDemoCode(data.demo_code || "");
      setStep("emailcode");
      if (data.demo_code) toast.success("Code (démo) : " + data.demo_code);
      else toast.success("Code envoyé par email ✅");
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  };

  const onVerifyEmail = async () => {
    if (emailCode.trim().length < 4) return toast.error("Entrez le code reçu");
    setLoading(true);
    try {
      const { data } = await api.post("/agent/email/verify-code", { email: email.trim().toLowerCase(), code: emailCode.trim() });
      login(data.token, data.agent);
      toast.success("✓");
      navigate("/equipe", { replace: true });
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  };

  const loginCode = async () => {
    if (code.trim().length !== 6) return toast.error("Entrez le code à 6 chiffres");
    setLoading(true);
    try {
      const { data } = await api.post("/agent/team-code", { code: code.trim() });
      login(data.token, data.agent);
      toast.success("✓");
      navigate("/equipe", { replace: true });
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const loginAgentCode = async () => {
    if (code.trim().length !== 6) return toast.error("Entrez votre code à 6 chiffres");
    setLoading(true);
    try {
      const { data } = await api.post("/agent/access-code/login", { code: code.trim() });
      login(data.token, data.agent);
      toast.success("✓");
      navigate("/equipe", { replace: true });
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#FDF8F0] flex flex-col">
      <div className="max-w-md w-full mx-auto px-5 py-6 flex-1 flex flex-col">
        <button data-testid="agent-back" onClick={() => navigate("/")} className="flex items-center gap-2 text-[#75807A] hover:text-[#1A1A1A] text-sm font-medium">
          <ArrowLeft className="w-4 h-4" /> {t("brand")}
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="mt-10 bg-white rounded-3xl border border-[#E2DFD8] p-7 shadow-[0_8px_40px_rgba(0,0,0,0.05)]"
        >
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: ACCENT }}>
            <Users className="w-7 h-7 text-white" />
          </div>
          <h1 className="mt-5 font-head font-black text-2xl tracking-tight text-[#1A1A1A]">{t("agent.login")}</h1>
          <p className="mt-1 text-[#75807A] text-sm">{t("agent.space")}</p>

          {step === "email" && (
            <div className="mt-7 space-y-4">
              <div>
                <label className="text-sm font-medium text-[#3B423E]">Votre email</label>
                <div className="relative mt-1.5">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#75807A]" />
                  <Input data-testid="agent-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vous@exemple.com" className="h-14 rounded-xl border-2 pl-11" onKeyDown={(e) => e.key === "Enter" && onSendEmail()} />
                </div>
              </div>
              <Button data-testid="agent-send-email" onClick={onSendEmail} disabled={loading} className="w-full h-14 rounded-xl text-white font-bold text-lg" style={{ background: ACCENT }}>
                {loading ? t("common.loading") : "Recevoir mon code par email"}
              </Button>
              <p className="text-xs text-[#75807A] text-center">Réservé aux agents approuvés. Pas encore agent ? Envoyez votre candidature depuis l'accueil.</p>
              <div className="flex items-center gap-3 py-1"><div className="h-px flex-1 bg-[#E2DFD8]" /><span className="text-xs text-[#75807A]">ou</span><div className="h-px flex-1 bg-[#E2DFD8]" /></div>
              <button data-testid="agent-access-code-link" onClick={() => { setStep("agentcode"); setCode(""); }} className="w-full h-14 rounded-xl border-2 font-bold text-lg inline-flex items-center justify-center gap-2 transition-colors" style={{ borderColor: ACCENT, color: ACCENT }}><KeyRound className="w-5 h-5" /> Connexion agent par code (6 chiffres)</button>
              <button data-testid="agent-code-access" onClick={() => { setStep("teamcode"); setCode(""); }} className="w-full text-center text-sm text-[#75807A] hover:text-[#D94F2B] font-medium inline-flex items-center justify-center gap-1.5 transition-colors"><KeyRound className="w-3.5 h-3.5" /> Accès responsable avec code</button>
            </div>
          )}

          {step === "emailcode" && (
            <div className="mt-7 space-y-4">
              <p className="text-sm text-[#75807A]">Entrez le code envoyé à <b>{email}</b>.</p>
              {demoCode && (
                <div className="rounded-xl bg-[#F0EBE1] border border-[#E2DFD8] p-4 flex items-center justify-between">
                  <div>
                    <div className="text-xs text-[#75807A] uppercase tracking-wide">Code (démo)</div>
                    <div className="font-head font-black text-2xl tracking-[0.3em] text-[#1A1A1A]" data-testid="agent-demo-code">{demoCode}</div>
                  </div>
                  <button data-testid="agent-use-demo-code" onClick={() => setEmailCode(demoCode)} className="text-sm font-semibold text-[#D94F2B] underline">{t("auth.useCode")}</button>
                </div>
              )}
              <Input data-testid="agent-emailcode-input" value={emailCode} onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" maxLength={6} placeholder="••••" className="h-14 rounded-xl border-2 text-center text-2xl tracking-[0.4em] font-bold" onKeyDown={(e) => e.key === "Enter" && onVerifyEmail()} />
              <Button data-testid="agent-verify-email" onClick={onVerifyEmail} disabled={loading} className="w-full h-14 rounded-xl text-white font-bold text-lg" style={{ background: ACCENT }}>
                {loading ? t("common.loading") : "Se connecter"}
              </Button>
              <div className="flex justify-between text-sm">
                <button data-testid="agent-email-back" onClick={() => { setStep("email"); setEmailCode(""); }} className="text-[#75807A] hover:text-[#1A1A1A]">{t("auth.back")}</button>
                <button data-testid="agent-resend-email" onClick={onSendEmail} className="text-[#D94F2B] font-medium">Renvoyer le code</button>
              </div>
            </div>
          )}

          {step === "teamcode" && (
            <div className="mt-7 space-y-4">
              <div>
                <h2 className="font-head font-bold text-lg text-[#1A1A1A]">Accès responsable</h2>
                <p className="text-sm text-[#75807A]">Entrez le code d'accès hebdomadaire (6 chiffres) visible dans votre tableau de bord Admin.</p>
              </div>
              <Input data-testid="agent-code-input" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" maxLength={6} placeholder="••••••" className="h-14 rounded-xl border-2 text-center text-2xl tracking-[0.5em] font-bold" onKeyDown={(e) => e.key === "Enter" && loginCode()} />
              <Button data-testid="agent-code-submit" onClick={loginCode} disabled={loading} className="w-full h-14 rounded-xl text-white font-bold text-lg" style={{ background: ACCENT }}>
                {loading ? t("common.loading") : "Accéder à l'espace Équipe"}
              </Button>
              <button data-testid="agent-code-back" onClick={() => { setStep("email"); setCode(""); }} className="text-sm text-[#75807A] hover:text-[#1A1A1A]">{t("auth.back")}</button>
            </div>
          )}

          {step === "agentcode" && (
            <div className="mt-7 space-y-4">
              <div>
                <h2 className="font-head font-bold text-lg text-[#1A1A1A]">Connexion agent</h2>
                <p className="text-sm text-[#75807A]">Entrez votre code d'accès à <b>6 chiffres</b> (fourni par votre responsable).</p>
              </div>
              <Input data-testid="agent-accesscode-input" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" maxLength={6} placeholder="••••••" className="h-16 rounded-xl border-2 text-center text-3xl tracking-[0.5em] font-bold" onKeyDown={(e) => e.key === "Enter" && loginAgentCode()} />
              <Button data-testid="agent-accesscode-submit" onClick={loginAgentCode} disabled={loading} className="w-full h-14 rounded-xl text-white font-bold text-lg" style={{ background: ACCENT }}>
                {loading ? t("common.loading") : "Accéder à l'espace Équipe"}
              </Button>
              <button data-testid="agent-accesscode-back" onClick={() => { setStep("email"); setCode(""); }} className="text-sm text-[#75807A] hover:text-[#1A1A1A]">{t("auth.back")}</button>
            </div>
          )}

          <div className="mt-6 flex items-center gap-2 text-xs text-[#75807A]">
            <ShieldCheck className="w-4 h-4 text-[#D94F2B]" /> {t("agent.notAuthorized").slice(0, 40)}
          </div>
        </motion.div>
      </div>
    </div>
  );
}

/* ---------------- Dashboard ---------------- */
function AgentDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [me, setMe] = useState(null);
  const [name, setName] = useState("");
  const [zone, setZone] = useState("");
  const [objectif, setObjectif] = useState("");
  const [qr, setQr] = useState("");
  const [link, setLink] = useState("");
  const [feed, setFeed] = useState([]);
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const [board, setBoard] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [activeMeeting, setActiveMeeting] = useState(null);
  const [showContractModal, setShowContractModal] = useState(false);
  const [notifOn, setNotifOn] = useState(notifPermission() === "granted");
  const [gps, setGps] = useState({ state: "init" });
  const [onDuty, setOnDuty] = useState(true);
  const [demoMode, setDemoMode] = useState(false);
  useEffect(() => { api.get("/promo").then((r) => setDemoMode(!!r.data.demo_mode)).catch(() => {}); }, []);
  const enableNotif = async () => {
    try { await enablePush(); setNotifOn(true); toast.success(t("notif.enabled")); }
    catch { toast.error(t("notif.denied")); }
  };

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/agent/me");
      setMe(data);
      if (!data.contract_signed && !sessionStorage.getItem("bsg_contract_prompt")) setShowContractModal(true);
      setName(data.name || ""); setZone(data.zone || ""); setObjectif(data.objectif || "");
      const target = `${window.location.origin}/inscription?agent=${data.agent_code}`;
      setLink(target);
      QRCode.toDataURL(target, { width: 1024, margin: 2, errorCorrectionLevel: "H", color: { dark: "#1A1A1A", light: "#FFFFFF" } }).then(setQr).catch(() => {});
    } catch (e) { /* handled by interceptor */ }
  }, []);

  const loadFeed = useCallback(() => {
    api.get("/agent/feed").then((r) => setFeed(r.data)).catch(() => {});
  }, []);

  const loadExtra = useCallback(() => {
    api.get("/agent/leaderboard").then((r) => setBoard(r.data || [])).catch(() => {});
    api.get("/notifications").then((r) => setAlerts(r.data.items || [])).catch(() => {});
    api.get("/agent/meetings").then((r) => setMeetings(r.data.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const mid = new URLSearchParams(window.location.search).get("meet");
    if (mid) setActiveMeeting(mid);
  }, []);

  useEffect(() => { loadMe(); loadFeed(); loadExtra(); const iv = setInterval(() => { loadFeed(); loadExtra(); }, 15000); return () => clearInterval(iv); }, [loadMe, loadFeed, loadExtra]);
  useEffect(() => { if (pushSupported() && notifPermission() === "granted") enablePush().catch(() => {}); }, []);

  // Live location ping every 5s + Wake Lock while the agent app is open (real-time map)
  useEffect(() => {
    if (!onDuty) { setGps({ state: "off" }); return; }
    let cancelled = false;
    const ping = async () => {
      try {
        const c = await getCurrentPosition();
        if (cancelled) return;
        if (c.accuracy != null && c.accuracy > 20000) { setGps({ state: "imprecise", acc: Math.round(c.accuracy) }); return; }
        await api.post("/agent/location", { lat: c.lat, lng: c.lng, accuracy: c.accuracy });
        setGps({ state: "ok", acc: Math.round(c.accuracy || 0), at: Date.now() });
      } catch (e) { if (!cancelled) setGps({ state: "error", msg: (e && e.message) || "GPS indisponible" }); }
    };
    ping();
    let wl = null;
    const acquireWL = async () => { wl = await acquireWakeLock(); };
    const onVis = () => { if (document.visibilityState === "visible") { acquireWL(); ping(); } };
    acquireWL();
    document.addEventListener("visibilitychange", onVis);
    const iv = setInterval(ping, 20000);
    return () => { cancelled = true; clearInterval(iv); document.removeEventListener("visibilitychange", onVis); try { wl && wl.release && wl.release(); } catch { /* ignore */ } };
  }, [onDuty]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/agent/profile", { name, zone, objectif });
      setMe((m) => ({ ...m, ...data }));
      toast.success(t("agent.profileSaved"));
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const download = () => { const a = document.createElement("a"); a.href = qr; a.download = `bentskin-go-${me?.agent_code}.png`; a.click(); };
  const copyLink = () => { navigator.clipboard?.writeText(link); toast.success(t("agent.linkCopied")); };
  const waShare = () => { window.open(`https://wa.me/?text=${encodeURIComponent(`${t("agent.waMsg")} ${link}`)}`, "_blank"); };
  const waRecap = () => {
    const txt = `📊 ${t("agent.space")}\n${me?.name || ""}\n${t("agent.thisMonth")}: ${me?.recruited_month ?? 0}\n${t("agent.total")}: ${me?.recruited_total ?? 0}\n${t("agent.bonus")}: ${fcfa(me?.bonus_total ?? 0)}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(txt)}`, "_blank");
  };

  const postMsg = async () => {
    if (!msg.trim()) return;
    try { await api.post("/agent/feed", { text: msg.trim() }); setMsg(""); loadFeed(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const deleteMsg = async (id) => {
    try { await api.delete(`/agent/feed/${id}`); loadFeed(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const goalNum = parseInt(String(me?.objectif || "").replace(/[^0-9]/g, ""), 10) || 0;
  const goalPct = goalNum > 0 ? Math.min(100, Math.round(((me?.recruited_month || 0) / goalNum) * 100)) : 0;

  if (me && me.contract_blocked) {
    return (
      <div className="min-h-screen bg-[#FDF8F0]">
        <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#FDF8F0]/80 border-b border-[#E2DFD8]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
            <span className="font-head font-extrabold text-lg tracking-tight text-[#1A1A1A]">{t("agent.space")}</span>
            <button data-testid="agent-logout" onClick={() => { logout(); navigate("/"); }} className="flex items-center gap-1.5 h-10 px-3 rounded-xl bg-white border border-[#E2DFD8] hover:border-[#D32F2F] hover:text-[#D32F2F] transition-colors text-sm font-medium"><LogOut className="w-4 h-4" /> <span className="hidden sm:inline">{t("agent.logout")}</span></button>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-5">
          <div data-testid="contract-blocked-banner" className="bg-[#D32F2F] text-white rounded-2xl p-5 flex items-start gap-3">
            <Lock className="w-6 h-6 shrink-0 mt-0.5" />
            <div>
              <div className="font-head font-black text-lg">Accès bloqué — contrat non signé</div>
              <p className="text-sm text-white/90 mt-1">Le délai de signature (7 jours) est dépassé. Signez votre contrat de travail ci-dessous pour réactiver l'accès à votre espace (recrutement, commissions, QR).</p>
            </div>
          </div>
          <SignContract onSigned={loadMe} />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDF8F0]">
      <button data-testid="agent-service-toggle" onClick={() => setOnDuty((v) => !v)} title="Activer/désactiver le suivi GPS (garde l'écran allumé)" className={`fixed z-[60] bottom-4 left-4 px-3 py-2 rounded-full text-xs font-semibold shadow-lg border flex items-center gap-2 bg-white ${!onDuty ? "border-[#E2DFD8] text-[#75807A]" : gps.state === "ok" ? "border-[#16a34a]/40 text-[#16a34a]" : gps.state === "error" ? "border-[#D32F2F]/40 text-[#D32F2F]" : gps.state === "imprecise" ? "border-[#F0C67A] text-[#8A5A00]" : "border-[#E2DFD8] text-[#75807A]"}`}>
        <span className={`w-2 h-2 rounded-full ${!onDuty ? "bg-[#9aa39d]" : gps.state === "ok" ? "bg-[#16a34a] animate-pulse" : gps.state === "error" ? "bg-[#D32F2F]" : gps.state === "imprecise" ? "bg-[#F26419]" : "bg-[#9aa39d]"}`} />
        {!onDuty ? "Service OFF — appuie pour activer le suivi" : gps.state === "ok" ? `En service · GPS ${gps.acc} m` : gps.state === "imprecise" ? "GPS imprécis — active la haute précision" : gps.state === "error" ? "GPS bloqué — autorise la localisation" : "Recherche GPS…"}
      </button>
      {/* Header */}
      <header className="print:hidden sticky top-0 z-40 backdrop-blur-xl bg-[#FDF8F0]/80 border-b border-[#E2DFD8]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button data-testid="agent-brand" onClick={() => navigate("/")} className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#F26419] to-[#D94F2B] flex items-center justify-center"><Users className="w-5 h-5 text-white" /></span>
            <span className="font-head font-extrabold text-lg tracking-tight text-[#1A1A1A]">{t("agent.space")}</span>
            {demoMode && <span data-testid="agent-demo-chip" title="Aucun SMS envoyé — le code s'affiche à l'écran" className="ml-1 inline-flex items-center rounded-full bg-[#FFF3E0] text-[#8A5A00] border border-[#F0C67A] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">Démo</span>}
          </button>
          <button data-testid="agent-logout" onClick={() => { logout(); navigate("/"); }} className="flex items-center gap-1.5 h-10 px-3 rounded-xl bg-white border border-[#E2DFD8] hover:border-[#D32F2F] hover:text-[#D32F2F] transition-colors text-sm font-medium">
            <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">{t("agent.logout")}</span>
          </button>
        </div>
      </header>

      {me && !me.contract_signed && (
        <div data-testid="contract-pending-banner" className="bg-[#D32F2F] text-white px-4 py-2.5 text-sm font-semibold flex items-center justify-center gap-2 text-center">
          <AlertTriangle className="w-4 h-4 shrink-0" /> Contrat en attente — signez votre contrat de travail{me.contract_deadline ? ` avant le ${new Date(me.contract_deadline).toLocaleDateString("fr-FR")}` : ""} pour garder l'accès.
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <div>
          <h1 className="font-head font-black text-2xl tracking-tight text-[#1A1A1A]" data-testid="agent-welcome">{t("agent.welcome")}, {me?.name || "Agent"} 👋</h1>
          <p className="text-[#75807A] text-sm">{t("agent.dashboard")} · {t("agent.zone")}: {me?.zone || "—"}</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          {[
            { label: t("agent.thisMonth"), val: me?.recruited_month ?? 0, icon: TrendingUp, c: "#D94F2B" },
            { label: t("agent.total"), val: me?.recruited_total ?? 0, icon: Bike, c: "#1A1A1A" },
            { label: "Passagers amenés", val: me?.passengers_total ?? 0, icon: Users, c: "#2563EB" },
            { label: t("agent.bonus"), val: fcfa(me?.bonus_total ?? 0), icon: Target, c: "#16a34a" },
          ].map((k, i) => (
            <div key={i} data-testid={`agent-stat-${i}`} className="bg-white rounded-2xl border border-[#E2DFD8] p-4 sm:p-5">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${k.c}20` }}><k.icon className="w-5 h-5" style={{ color: k.c }} /></div>
              <div className="font-head font-black text-xl sm:text-2xl mt-3">{k.val}</div>
              <div className="text-xs text-[#75807A] mt-0.5">{k.label}</div>
            </div>
          ))}
        </div>
        <p className="text-xs text-[#75807A] -mt-3">{t("agent.recruited")} · {fcfa(me?.bonus_per ?? 0)} {t("agent.perRecruit")}</p>

        {goalNum > 0 && (
          <div data-testid="agent-goal-progress" className="bg-white rounded-2xl border border-[#E2DFD8] p-5">
            <div className="flex items-center justify-between text-sm"><span className="font-semibold flex items-center gap-2"><Target className="w-4 h-4 text-[#D94F2B]" /> {t("agent.goalTitle")}</span><span className="text-[#75807A]">{me?.recruited_month || 0} / {goalNum}</span></div>
            <div className="mt-3 h-3 rounded-full bg-[#F0EBE1] overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-[#F26419] to-[#D94F2B] transition-all duration-500" style={{ width: `${goalPct}%` }} /></div>
            <div className="mt-1 text-[11px] text-[#75807A]">{goalPct}% {t("agent.goalReached")}</div>
          </div>
        )}

        {meetings.length > 0 && (
          <div data-testid="agent-meetings-card" className="bg-white rounded-2xl border-2 border-[#D94F2B]/40 p-5">
            <h2 className="font-head font-bold text-lg flex items-center gap-2"><Video className="w-5 h-5 text-[#D94F2B]" /> Réunions vidéo (Mboa Meet)</h2>
            <div className="mt-3 space-y-2">
              {meetings.map((m) => (
                <div key={m.id} data-testid={`agent-meeting-${m.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#E2DFD8] p-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-[#1A1A1A]">{m.title}</div>
                    <div className="text-xs text-[#75807A]">Avec {m.host_name} · {new Date(m.scheduled_at).toLocaleString("fr-FR")}</div>
                  </div>
                  <button data-testid={`agent-meeting-join-${m.id}`} onClick={() => setActiveMeeting(m.id)} className="inline-flex items-center gap-2 h-10 px-5 rounded-xl bg-[#F26419] hover:bg-[#d9540f] text-white text-sm font-bold transition-colors"><Video className="w-4 h-4" /> Rejoindre</button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-6">
          <div data-testid="agent-leaderboard" className="bg-white rounded-2xl border border-[#E2DFD8] p-6">
            <h2 className="font-head font-bold text-lg flex items-center gap-2"><Trophy className="w-5 h-5 text-[#F26419]" /> {t("agent.leaderboard")}</h2>
            {me?.rank === 1 && (me?.recruited_month ?? 0) > 0 && (
              <div data-testid="agent-top-badge" className="mt-4 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#F26419] to-[#D94F2B] text-white px-4 py-2 text-sm font-bold shadow-[0_4px_16px_rgba(242,100,25,0.35)]">
                <Trophy className="w-4 h-4" /> {t("agent.topBadge")}
              </div>
            )}
            {me?.rank && <p className="mt-1 text-sm text-[#75807A]">{t("agent.yourRank")} <span className="font-bold text-[#D94F2B]">#{me.rank}</span> / {me?.agents_count || board.length}</p>}
            <div className="mt-4 space-y-2">
              {board.slice(0, 10).map((a) => (
                <div key={a.id} data-testid={`lb-row-${a.id}`} className={`flex items-center justify-between rounded-xl px-3 py-2 ${a.id === me?.id ? "bg-[#F26419]/10 border border-[#D94F2B]/30" : "bg-[#FDF8F0]"}`}>
                  <div className="flex items-center gap-3">
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black ${a.rank === 1 ? "bg-[#F26419] text-white" : a.rank === 2 ? "bg-[#C0C0C0] text-white" : a.rank === 3 ? "bg-[#CD7F32] text-white" : "bg-[#E2DFD8] text-[#75807A]"}`}>{a.rank}</span>
                    <div><div className="text-sm font-semibold">{a.name}</div><div className="text-[11px] text-[#75807A]">{a.zone}</div></div>
                  </div>
                  <div className="text-sm font-bold">{a.recruited_month}</div>
                </div>
              ))}
              {board.length === 0 && <p className="text-sm text-[#75807A] text-center py-4">—</p>}
            </div>
          </div>
          <div data-testid="agent-alerts" className="bg-white rounded-2xl border border-[#E2DFD8] p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-head font-bold text-lg flex items-center gap-2"><Bell className="w-5 h-5 text-[#D94F2B]" /> {t("agent.alerts")}</h2>
              <div className="flex gap-2">
                <button data-testid="agent-wa-recap" onClick={waRecap} className="text-xs font-semibold px-3 py-1.5 rounded-full border border-[#25D366] text-[#25D366] hover:bg-[#25D366]/5 transition-colors">{t("agent.waRecap")}</button>
                <button data-testid="agent-enable-push" onClick={enableNotif} disabled={notifOn} className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${notifOn ? "bg-[#F0EBE1] border-[#E2DFD8] text-[#75807A]" : "bg-white border-[#D94F2B] text-[#D94F2B] hover:bg-[#F26419]/5"}`}>{notifOn ? t("notif.enabled") : t("notif.enable")}</button>
              </div>
            </div>
            <div className="mt-4 space-y-2 max-h-[360px] overflow-y-auto no-scrollbar">
              {alerts.length === 0 && <p className="text-sm text-[#75807A] text-center py-6">{t("agent.alertsEmpty")}</p>}
              {alerts.map((n) => (
                <div key={n.id} data-testid={`alert-${n.id}`} className={`rounded-xl border p-3 ${n.read ? "border-[#E2DFD8]" : "border-[#D94F2B]/40 bg-[#F26419]/5"}`}>
                  <div className="text-sm font-semibold">{n.title}</div>
                  <div className="text-xs text-[#3B423E] mt-0.5 whitespace-pre-wrap break-words">{n.body}</div>
                  <div className="text-[11px] text-[#B8B2A7] mt-1">{new Date(n.created_at).toLocaleString("fr-FR")}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Profile form */}
          <div className="bg-white rounded-2xl border border-[#E2DFD8] p-6">
            <h2 className="font-head font-bold text-lg text-[#1A1A1A] flex items-center gap-2"><MapPin className="w-5 h-5 text-[#D94F2B]" /> {t("agent.profile")}</h2>
            <div className="mt-4 space-y-3">
              <div>
                <label className="text-xs text-[#75807A]">{t("agent.name")}</label>
                <Input data-testid="agent-name" value={name} onChange={(e) => setName(e.target.value)} className="h-12 rounded-xl border-2 mt-1" />
              </div>
              <div>
                <label className="text-xs text-[#75807A]">{t("agent.zone")}</label>
                <Input data-testid="agent-zone" value={zone} onChange={(e) => setZone(e.target.value)} placeholder={t("agent.zonePh")} className="h-12 rounded-xl border-2 mt-1" />
              </div>
              <div>
                <label className="text-xs text-[#75807A]">{t("agent.objectif")}</label>
                <Input data-testid="agent-objectif" value={objectif} onChange={(e) => setObjectif(e.target.value)} placeholder={t("agent.objectifPh")} className="h-12 rounded-xl border-2 mt-1" />
              </div>
              <Button data-testid="agent-save-profile" onClick={saveProfile} disabled={saving} className="w-full h-12 rounded-xl bg-[#1A1A1A] text-white font-bold">{saving ? t("common.loading") : t("agent.saveProfile")}</Button>
            </div>
          </div>

          <ExpensesCard />

          <SignContract onSigned={loadMe} />

          {/* QR card */}
          <div data-testid="agent-qr-card" className="bg-white rounded-2xl border border-[#E2DFD8] p-6 text-center print:border-2">
            <h2 className="print:hidden font-head font-bold text-lg text-[#1A1A1A] flex items-center gap-2 justify-center"><Users className="w-5 h-5 text-[#D94F2B]" /> {t("agent.myQr")}</h2>
            <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-[#F0EBE1] px-3 py-1 text-sm font-bold text-[#1A1A1A]" data-testid="agent-code">{me?.agent_code}</div>
            <div className="mt-4 rounded-2xl bg-[#FDF8F0] p-4 inline-block">
              {qr ? <img data-testid="agent-qr-img" src={qr} alt="QR" className="w-52 h-52 sm:w-60 sm:h-60" /> : <div className="w-52 h-52 sm:w-60 sm:h-60 animate-pulse bg-[#E2DFD8] rounded-xl" />}
            </div>
            <p className="print:hidden mt-3 text-[#75807A] text-sm">{t("agent.qrDesc")}</p>
            <div className="print:hidden mt-4 flex flex-col sm:flex-row gap-3 justify-center">
              <button data-testid="agent-download-qr" onClick={download} disabled={!qr} className="h-12 px-5 rounded-xl bg-[#F26419] hover:bg-[#D85312] disabled:opacity-50 text-white font-bold inline-flex items-center justify-center gap-2 transition-colors"><Download className="w-4 h-4" /> {t("agent.downloadPng")}</button>
              <button data-testid="agent-print-qr" onClick={() => window.print()} className="h-12 px-5 rounded-xl bg-white border border-[#E2DFD8] hover:border-[#D94F2B] text-[#1A1A1A] font-bold inline-flex items-center justify-center gap-2 transition-colors"><Printer className="w-4 h-4" /> {t("agent.print")}</button>
              <button data-testid="agent-whatsapp-qr" onClick={waShare} disabled={!link} className="h-12 px-5 rounded-xl bg-[#25D366] hover:bg-[#1EBE5B] disabled:opacity-50 text-white font-bold inline-flex items-center justify-center gap-2 transition-colors"><MessageCircle className="w-4 h-4" /> {t("agent.waShare")}</button>
            </div>
            <button data-testid="agent-copy-link" onClick={copyLink} className="print:hidden mt-3 inline-flex items-center gap-2 text-[#75807A] hover:text-[#D94F2B] text-sm font-medium transition-colors"><Copy className="w-4 h-4" /> {t("agent.copyLink")}</button>
          </div>
        </div>

        <PromoPoster agentCode={me?.agent_code} />

        {/* Team feed */}
        <div className="print:hidden bg-white rounded-2xl border border-[#E2DFD8] p-6">
          <h2 className="font-head font-bold text-lg text-[#1A1A1A] flex items-center gap-2"><Send className="w-5 h-5 text-[#D94F2B]" /> Annonces de l'équipe</h2>
          <p className="mt-1 text-xs text-[#75807A]">Annonces de l'administration. Pour signaler quelque chose, utilisez « Notes & notes de frais » ci-dessus.</p>
          <div className="mt-4 space-y-3 max-h-[420px] overflow-y-auto no-scrollbar">
            {feed.length === 0 && <p className="text-sm text-[#75807A] text-center py-6">{t("agent.feedEmpty")}</p>}
            {feed.map((m) => (
              <div key={m.id} data-testid={`feed-msg-${m.id}`} className="rounded-xl border border-[#E2DFD8] p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-bold ${m.author_role === "admin" ? "text-[#D94F2B]" : "text-[#1A1A1A]"}`}>{m.author_name}{m.author_role === "admin" ? " · Admin" : ""}</span>
                    {m.author_zone && <span className="text-[11px] text-[#75807A]">· {m.author_zone}</span>}
                  </div>
                </div>
                <p className="mt-1 text-sm text-[#3B423E] whitespace-pre-wrap break-words">{m.text}</p>
                <div className="mt-1 text-[11px] text-[#B8B2A7]">{new Date(m.created_at).toLocaleString("fr-FR")}</div>
              </div>
            ))}
          </div>
        </div>
      </main>
      {activeMeeting && <MeetingRoom meetingId={activeMeeting} onClose={() => { setActiveMeeting(null); loadExtra(); }} />}
      {showContractModal && (
        <div data-testid="contract-modal" className="fixed inset-0 z-[95] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 text-center">
            <div className="mx-auto w-14 h-14 rounded-2xl bg-[#F26419]/15 flex items-center justify-center"><PenLine className="w-7 h-7 text-[#D94F2B]" /></div>
            <h3 className="mt-4 font-head font-black text-xl">Signature du contrat obligatoire</h3>
            <p className="mt-2 text-sm text-[#75807A]">Pour activer et conserver l'accès à votre espace agent, vous devez signer votre contrat de travail.{me?.contract_deadline ? ` Délai : jusqu'au ${new Date(me.contract_deadline).toLocaleDateString("fr-FR")}.` : ""}</p>
            <div className="mt-6 flex gap-3">
              <button data-testid="contract-modal-later" onClick={() => { sessionStorage.setItem("bsg_contract_prompt", "1"); setShowContractModal(false); }} className="flex-1 h-11 rounded-xl bg-white border border-[#E2DFD8] text-[#75807A] font-semibold">Plus tard</button>
              <button data-testid="contract-modal-sign" onClick={() => { sessionStorage.setItem("bsg_contract_prompt", "1"); setShowContractModal(false); document.querySelector('[data-testid=sign-contract-card]')?.scrollIntoView({ behavior: "smooth" }); }} className="flex-1 h-11 rounded-xl bg-[#F26419] text-white font-bold">Signer maintenant</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
